import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Card, DataStatus} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {useEffect, useState} from 'react';

import {isNotNil} from 'ramda';

import {
  PriceMapRequestBody,
  SourcingVehicleDetailResponseBody,
  useSourcingGetPriceMapQuery,
  useSourcingGetPriceMapStatisticsQuery,
} from '@dms/api/core';
import featureFlags from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useSourcingCurrency} from '../../../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../../../hooks/useSourcingTargetCountry';
import {ComparisonFields} from './components/ComparisonFields/ComparisonFields';
import {ChartCheckboxes} from './components/PriceMap/components/ChartCheckboxes';
import {SourcingVehicleCard} from './components/PriceMap/components/SourcingVehicleCard';
import {PriceMap} from './components/PriceMap/PriceMap';

const ITEMS_PER_PAGE = 1000;

interface PriceMapTabProps extends TestIdProps {
  vehicle: SourcingVehicleDetailResponseBody;
}

export function PriceMapTab({vehicle, ...props}: PriceMapTabProps) {
  const [checkboxes, setCheckboxes] = useState<{
    onStockChecked: boolean;
    soldRecentlyChecked: boolean;
    soldChecked: boolean;
  }>({
    onStockChecked: true,
    soldRecentlyChecked: true,
    soldChecked: true,
  });
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | undefined>();

  const currency = useSourcingCurrency()?.code;
  const sellerCountry = useSourcingTargetCountry()?.code;

  useEffect(() => {
    setSelectedVehicleId(undefined);
  }, [vehicle]);

  const priceMapRequestBody: PriceMapRequestBody | undefined = isNotNil(vehicle.sourcingVehicle)
    ? {
        size: ITEMS_PER_PAGE,
        query_price: vehicle.sourcingVehicle.price?.withVat ?? 0,
        fuelType: isNotNil(vehicle.sourcingVehicle.fuelType?.key)
          ? [vehicle.sourcingVehicle.fuelType?.key]
          : undefined,
        transmission: isNotNil(vehicle.sourcingVehicle.transmission?.key)
          ? [vehicle.sourcingVehicle.transmission?.key]
          : undefined,
        bodyStyle: isNotNil(vehicle.sourcingVehicle.carStyle?.key)
          ? [vehicle.sourcingVehicle.carStyle?.key]
          : undefined,
        drive: isNotNil(vehicle.sourcingVehicle.driveType?.key)
          ? [vehicle.sourcingVehicle.driveType?.key]
          : undefined,
        firstRegistrationOnYear: isNotNil(vehicle.sourcingVehicle.year)
          ? {
              gte: vehicle.sourcingVehicle.year,
              lte: vehicle.sourcingVehicle.year,
            }
          : undefined,
        power: isNotNil(vehicle.sourcingVehicle.power)
          ? {
              gte: vehicle.sourcingVehicle.power,
              lte: vehicle.sourcingVehicle.power,
            }
          : undefined,
        hideStockCars: !checkboxes.onStockChecked,
        hideSoldIn30: !checkboxes.soldRecentlyChecked,
        hideSoldIn90: !checkboxes.soldChecked,
      }
    : undefined;

  const {
    data,
    isLoading: isLoadingPriceMap,
    isError: isErrorPriceMap,
  } = useSourcingGetPriceMapQuery(
    isNotNil(priceMapRequestBody)
      ? {
          externalId: vehicle.sourcingVehicle.adId,
          priceMapRequestBody,
        }
      : skipToken
  );

  const statisticsRequestBody = {
    year: vehicle.sourcingVehicle?.year ?? 0,
    power: vehicle.sourcingVehicle?.power ?? 0,
    mileage: vehicle.sourcingVehicle?.mileage ?? 0,
    currency: currency ?? 'CZK',
    sellerCountry: sellerCountry ?? 'CZ',
    make: vehicle.sourcingVehicle?.make?.key ?? '',
    model: vehicle.sourcingVehicle?.model?.key ?? '',
    drive: vehicle.sourcingVehicle?.driveType?.key ?? '',
    fuelType: vehicle.sourcingVehicle?.fuelType?.key ?? '',
    carStyle: vehicle.sourcingVehicle?.carStyle?.key ?? '',
    transmission: vehicle.sourcingVehicle?.transmission?.key ?? '',
  };

  const {
    data: statistics,
    isLoading: isLoadingStatistics,
    isError: isErrorStatistics,
  } = useSourcingGetPriceMapStatisticsQuery(statisticsRequestBody, {
    skip: !isFeatureEnabled(featureFlags.SOURCING_PRICE_MAP_STATISTICS),
  });
  const isLoading = isLoadingPriceMap || isLoadingStatistics;
  const isError = isErrorPriceMap || isErrorStatistics;

  const cars = data?.cars.filter((car) => car.ad_id !== vehicle.sourcingVehicle.adId) ?? [];

  return (
    <VStack spacing={4} width="100%">
      <DataStatus isLoading={isLoading} isError={isError} minHeight="100%">
        <Show whenFeatureEnabled={featureFlags.SOURCING_PRICE_MAP_STATISTICS}>
          <ComparisonFields
            vehicle={vehicle}
            isLoading={isLoadingStatistics}
            statistics={statistics ?? {}}
          />
        </Show>

        <Card
          title={i18n.t('general.labels.priceMap')}
          tooltip={i18n.t('entity.vehicle.labels.priceMapTooltip')}
        >
          <VStack spacing={4} height={120}>
            <ChartCheckboxes
              values={checkboxes}
              onChange={setCheckboxes}
              cars={cars}
              data-testid={suffixTestId('chartCheckboxes', props)}
            />
            <PriceMap
              cars={cars}
              queryPriceIndex={data?.query_price_index ?? 0}
              vehicle={vehicle}
              selectedVehicleId={selectedVehicleId ?? null}
              setSelectedVehicleId={(id) => {
                setSelectedVehicleId(id ?? undefined);
              }}
              data-testid={suffixTestId('priceMap', props)}
            />
          </VStack>
        </Card>

        <Show when={isNotNil(selectedVehicleId)}>
          <SourcingVehicleCard
            externalId={selectedVehicleId!}
            data-testid={suffixTestId('sourcingVehicleCard', props)}
          />
        </Show>
      </DataStatus>
    </VStack>
  );
}
